/**
 * Module 5: Emotion Is Information
 *
 * In which Philemon comes to practise being composed, and leaves without
 * having practised it.
 * Primary character: Philemon | Supporting: Xenia, Theron
 *
 * REWORKED 2026-10-04 to the six rules of docs/CONTENT_REVIEW_2026-10-04.md.
 * This was already the strongest evening, so the scene is the old one with
 * the boxes taken out of it and three of its four morals cut.
 *
 *   - What happens: Eudoros died this morning. The rites are tomorrow,
 *     Philemon is to speak, and he has come to practise being composed.
 *   - Who is wrong: Philemon, who gave his father's eulogy without a tremor
 *     twenty years ago and has a speech ready to do it again. He does not
 *     promise anything tonight; he leaves hiding his face. Theron is wrong
 *     too, and stays so: to him composure at the rites is manners.
 *   - The one line that stands alone: Xenia's "The grief is the tribute,
 *     Philemon." Nobody explains it afterwards. Do not add to it.
 *   - The technique: name what you feel in one plain sentence, and let it be
 *     heard. Philemon does it badly (it comes out as the speech), then
 *     plainly. The reader meets it in the turn, in exercise 1, and in
 *     exercise 2 with the Feeling Compass.
 *   - No jokes this evening, and the reader's turn asks; it does not test.
 *     No reply tells anyone how to grieve.
 *
 * CONTINUITY this evening must keep (other evenings depend on it):
 *   - We never see the eulogy, and nothing here may say whether his voice
 *     breaks. Evening 6 opens on that ("My voice broke on the third
 *     sentence"), and on Xenia's "Two eulogies, twenty years apart. Your
 *     mother would have known him from this one."
 *   - His mother's words, exactly: "it didn't sound like you knew him".
 *     Carried for twenty years.
 *   - Eudoros's words from Evening 2: "Now I should like to hear yours."
 *   - His father's face on every audience (planted in Evening 1).
 *   - The paragraph that points to the support lines on the Resources page
 *     stays word for word, just before the summary.
 *
 * Every study below was checked against its abstract or record on 2026-10-04;
 * see docs/audits/finding-your-voice-2026-10/register-modules-4-6.md. Nothing
 * was added to them here; they were only shortened.
 */

export const MODULE_05_CONTENT = [
  // --- The news ---
  {
    type: 'narration',
    text: 'You know before you reach your cushion that something is wrong. The courtyard is too quiet for this hour: no guests, no bustle. Someone has been turning people away at the gate. Philemon stands at the far wall with his back to the room. Xenia is watching him the way you watch a bowl balanced at the edge of a table. Theron comes in behind you, loud with harbour news, and stops mid-word.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'Eudoros died this morning. Philemon\'s teacher.',
  },
  {
    type: 'narration',
    text: 'The name lands on you too. The man who coaxed Philemon back to speaking when everyone else had given up. The man who said: now I should like to hear yours.',
  },
  {
    type: 'narration',
    text: 'A long moment passes. Then Philemon turns. His eyes are red-rimmed, but his voice is steady. Too steady.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'I\'m sorry. I shouldn\'t have come. I\'m not fit company tonight.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'You are always fit company. Sit.',
  },
  {
    type: 'narration',
    text: 'He does, heavily, like a man carrying more than his own weight.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'The elders have asked me to speak at his rites. Tomorrow. And I came here tonight because I intend to practise being composed.',
  },
  {
    type: 'narration',
    text: 'He says the word as though it were armour.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Then practise. That is what we are for. Stand up, give it to us, and I will tell you where it wobbles.',
  },

  // --- The other eulogy ---
  {
    type: 'heading',
    text: 'The other eulogy',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'Before he does. Philemon, when you say composed, what does that look like, tomorrow?',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Controlled. Dignified. Honouring him with eloquence. Not falling apart in front of everyone.',
  },
  {
    type: 'narration',
    text: 'Xenia says nothing. In her hands a silence makes room, and at last Philemon speaks into it.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'I have done this before, you know. Years ago. For my father.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'I didn\'t know that.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Nobody does. It was, by every measure I cared about then, a triumph. Meticulously prepared. Eloquent. Beautifully delivered. And it was a lie.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'How so?',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Because I delivered it as though I were reviewing a text, not burying my father. Every audience wore his face in those days, and that one most of all. I was so afraid of weeping in front of the assembly that I stripped every trace of feeling from my words. Afterwards my mother took my hands and said: "That was lovely, Philemon. But it didn\'t sound like you knew him."',
  },
  {
    type: 'narration',
    text: 'He pauses. His voice, when it comes, is rough.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'She was right. I have carried her sentence for twenty years. And tomorrow...',
  },
  {
    type: 'narration',
    text: 'He stops. The unfinished sentence stands in the middle of the courtyard, and everyone can see the shape of it.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'And tomorrow you are planning to do it again.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'And why should he not? The widow has enough to carry tomorrow without carrying him as well. A man holds together for the length of one speech. That is manners.',
  },
  {
    type: 'narration',
    text: 'Xenia lets that stand. She has not taken her eyes off Philemon.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'What are you afraid will happen tomorrow?',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'That I\'ll weep in front of a hundred people. That I\'ll start speaking and the words will crack open and everyone will see how undone I am.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'And would that be so terrible?',
  },
  {
    type: 'narration',
    text: 'Philemon looks up, startled.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'You will be speaking of a man who heard a voice in you when you could not hear it yourself. Who died this morning. If you speak of him with perfect composure, eloquent, dignified, at a careful distance, what will that say? That his loss is manageable? Tidy? That he never quite reached you?',
  },
  {
    type: 'narration',
    text: 'She leans forward, and there is nothing soft about her gentleness now.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'The grief is the tribute, Philemon.',
  },
  {
    type: 'narration',
    text: 'Nobody speaks. The lamps hiss quietly. Theron has an answer ready, and you watch him decide to keep it.',
  },

  // --- His own question ---
  {
    type: 'heading',
    text: 'His own question',
  },
  {
    type: 'narration',
    text: 'When Philemon speaks again, it is to his own hands.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'There is a question I put to every student before they speak. Never mind what you will say. What do you feel? One sentence, and a plain one. I have never once put it to myself.',
  },
  {
    type: 'dialogue',
    character: 'xenia',
    speaker: 'Xenia',
    text: 'And if you did?',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'I should say that the city is poorer tonight by one great teacher, and that those of us whom he... No. That is the speech.',
  },
  {
    type: 'narration',
    text: 'He starts again, slower.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'I miss him. I am afraid of tomorrow. And under both, I think, I am grateful. That I was his student at all.',
  },
  {
    type: 'narration',
    text: 'His voice is not steady when he says it, and he does not steady it. He looks up: at Xenia, at Theron, at you.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Good. Say that here, tonight, to us. Tomorrow, say the speech.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'I wrote the speech this afternoon. It is very good. He is not in it.',
  },
  {
    type: 'narration',
    text: 'It is quiet for a while after that. Then Xenia turns to you, and you understand that she has not once forgotten you were here.',
  },
  {
    type: 'yourTurn',
    id: 'm5-what-you-keep-out',
    character: 'xenia',
    speaker: 'Xenia',
    prompt: 'You have sat through a hard evening, and you did not have to. I will not ask what it has stirred in you. That is yours. Only this, if you care to answer: when you speak, what do you keep out of your voice?',
    options: [
      {
        label: 'That I am nervous.',
        reply: {
          character: 'philemon',
          speaker: 'Philemon',
          text: 'I kept mine out for thirty years, and the effort took more breath than the speaking did. Before you begin, say it once, to yourself or aloud: I am nervous, because this matters to me. Then begin.',
        },
      },
      {
        label: 'That I am angry.',
        reply: {
          character: 'theron',
          speaker: 'Theron',
          text: 'That one I know. Swallow it and it comes out anyway, as a smile with teeth in it, and the buyer walks. Say it once, flat: this is wrong, and I mind. Then say what you want.',
        },
      },
      {
        label: 'How much I care. Or how sad I am.',
        reply: {
          character: 'xenia',
          speaker: 'Xenia',
          text: 'Then I will not tell you what to do with it. It is yours. I can only say what I have seen it cost, kept out altogether: the room cannot tell you cared. One true sentence is enough. Would you risk one?',
        },
      },
      {
        label: 'How glad I am. How much I love the thing.',
        reply: {
          character: 'philemon',
          speaker: 'Philemon',
          text: 'For years I taught the poems I loved best as though I were finding fault with them, for fear of looking foolish. My students never guessed. Say it once, early: I love this. Then be as careful as you like.',
        },
      },
    ],
  },
  {
    type: 'exercise',
    number: 1,
    title: 'One plain sentence',
    instructions: 'Think of the next time you will speak about something that matters to you. It need not be a sad thing. Then do what Philemon did, and expect your first attempt to come out as the speech.',
    prompts: [
      'What will you be speaking about, and to whom?',
      'What do you feel about it? Write one plain sentence, in the words you would use to a friend. "I miss him." "I am angry about this." "I love this work."',
      'Read it back. If it sounds like something you would say from a platform, try again, shorter.',
      'You do not have to say that sentence to anyone. Decide only whether you are willing for it to be heard in your voice.',
    ],
  },

  // --- At the gate ---
  {
    type: 'heading',
    text: 'At the gate',
  },
  {
    type: 'narration',
    text: 'Philemon rises to leave, earlier than usual. Tomorrow is close. At the gate he stops beside you and seems about to say something. He rests a hand on your shoulder instead.',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'Philemon.',
  },
  {
    type: 'dialogue',
    character: 'philemon',
    speaker: 'Philemon',
    text: 'Yes?',
  },
  {
    type: 'dialogue',
    character: 'theron',
    speaker: 'Theron',
    text: 'We will be there tomorrow. All of us.',
  },
  {
    type: 'narration',
    text: 'Philemon nods once, quickly, before his face can betray him, and is gone into the dark.',
  },

  {
    type: 'divider',
  },

  // --- The one box, after the scene has landed ---
  {
    type: 'research',
    title: 'What hiding costs',
    source: 'Gross, Psychophysiology, 2002',
    paragraphs: [
      'From the 1990s on, the Stanford psychologist James Gross and his colleagues ran experiments on what happens when people hide what they feel. Volunteers watched an emotional film or set of pictures, and half were told to let nothing show.',
      'Their faces gave less away. But across the studies the feeling itself was not reduced, their hearts and blood vessels worked harder, and afterwards they remembered less of what they had seen. Nobody in these studies was giving a speech, so the step to speaking is ours.',
    ],
  },

  // --- To take away ---
  {
    type: 'heading',
    text: 'Before you next speak',
  },
  {
    type: 'exercise',
    number: 2,
    title: 'Name it, then speak',
    instructions: 'Before you next speak about something that matters to you, however small the occasion, stop for one breath and do this.',
    prompts: [
      'Name what you feel in one plain sentence. Use an exact word: anxious, hopeful, angry, tender, proud, sad. "Fine" is not a feeling.',
      'Ask what the feeling tells you about why this matters.',
      'Decide to let it be heard. That does not mean announcing it. It means not flattening your voice to keep it out.',
      'Afterwards, note what happened: to your voice, and to the people listening. Then do it again the next time.',
    ],
  },
  {
    type: 'toolLink',
    toolId: 'feeling-compass',
    title: 'Feeling Compass',
    text: 'Philemon\'s tool takes you through the same check: a short pause, a grid of feeling words to tap, and a line for what the feeling is telling you. You can save each check-in and look back over them.',
  },

  // --- The research, for whoever wants it ---
  {
    type: 'notes',
    title: 'What we know now',
    intro: 'Xenia can only tell Philemon what she has seen. Here is what has been tested since, and where the step to speaking is ours.',
    items: [
      {
        title: 'The cost reaches the other person',
        paragraphs: [
          'In a 2003 study led by Emily Butler, pairs of women who had never met talked over an upsetting topic, and in some pairs one woman had been told to hide her feelings. Those conversations had less rapport, and the blood pressure of the woman on the other side rose more.',
          'We think this is part of why an over-controlled speaker can seem flat or distant: the room picks up the effort of holding it in. The study did not test speakers, so that step is ours.',
        ],
        source: 'Butler and colleagues, Emotion, 2003',
      },
      {
        title: 'People tend to warm to a feeling that shows',
        paragraphs: [
          'In a study published in 2016, Allison Tackman and Sanjay Srivastava at the University of Oregon had people form impressions of strangers who were watching funny or sad film clips, some letting their reactions show and some hiding them. The ones who hid their feelings were judged less agreeable and less outgoing, and people were less interested in getting to know them.',
          'None of this means performing emotion, or manufacturing a feeling for effect. The study compared people who hid what they felt with people who let it show, and that is the only step we are asking of you.',
        ],
        source: 'Tackman and Srivastava, Journal of Personality and Social Psychology, 2016',
      },
      {
        title: 'The willingness to be seen',
        paragraphs: [
          'Brené Brown is a research professor at the University of Houston who has spent more than two decades studying courage, vulnerability, shame and empathy. Her method is qualitative: hundreds of long interviews, sorted for what people with a strong sense of love and belonging have in common. One thing they shared, she reported, was a willingness to be seen with no guarantee of how it would go.',
          'Brown\'s interviews were about people\'s lives, not about audiences, and she did not test speakers. Applying her idea to speaking is our step, not her finding: we think a speaker who can admit difficulty, doubt or feeling is easier to trust than one who projects invulnerability. That is no licence to overshare, or to use emotion as a lever.',
        ],
        source: 'Brené Brown, The Power of Vulnerability, TEDxHouston, 2010',
      },
      {
        title: 'When the feeling takes over',
        paragraphs: [
          'This last note is our own advice and not a study. Letting a feeling be heard is different from being overwhelmed by it, and being overwhelmed can stop you speaking at all. If you feel that coming (throat closing, vision blurring, thoughts fragmenting), that is your signal to pause, not to push through. Take a breath. Take another. Return to your body before returning to your words.',
        ],
      },
    ],
  },

  // This evening is about a death. The support lines themselves live on the
  // Resources page, from the fleet's shared table; no number is ever typed
  // into content. Keep this paragraph word for word.
  {
    type: 'paragraph',
    text: 'This evening is about a death, and it may land close to one of your own. If it stirs up more than you want to carry alone, tell someone you trust or talk to a doctor. The Resources page lists support lines for Australia, New Zealand, India, the UK, Ireland, Canada and the US.',
  },

  // --- Module Summary ---
  {
    type: 'summary',
    characterId: 'philemon',
    points: [
      'Hiding a feeling does not make it go away. It takes effort, and that effort comes out of what you have left for thinking and connecting.',
      'Before you speak, name what you feel in one plain sentence. If it comes out as the speech, try again.',
      'Then let it be heard. You do not have to announce it; you only have to leave it in your voice.',
    ],
    nextPreview: {
      dialogue: [
        {
          character: 'xenia',
          text: 'Come back after the rites. Whatever happens there, he will want to find the lamps lit.',
        },
        {
          character: 'theron',
          text: 'And the gathering has not moved. Daria is mine next. Come ready to stand.',
        },
      ],
    },
  },
];

export const MODULE_05_EXERCISES = [
  {
    number: 1,
    title: 'One plain sentence',
    description: 'Name what you feel about the next thing you have to say, in one plain sentence.',
    xpReward: 20,
  },
  {
    number: 2,
    title: 'Name it, then speak',
    description: 'Before you speak, name the feeling and decide to let it be heard.',
    xpReward: 20,
  },
];

export const MODULE_05_SUMMARY = {
  title: 'Emotion Is Information',
  keyInsight: 'Name what you feel, and let it be heard.',
  characterQuote: {
    character: 'xenia',
    text: 'The grief is the tribute, Philemon.',
  },
  takeaways: [
    'Hiding a feeling costs effort that you need for thinking and connecting.',
    'Before you speak, name what you feel in one plain sentence.',
    'Then let it be heard. You do not have to announce it.',
  ],
};
